import React, { useEffect, useRef, useState } from 'react';

import { Box, Flex, Tooltip } from '@chakra-ui/react';
import { Add } from '@mesh.ai/rabbit';
import { useDispatch } from 'react-redux';
import { components } from 'react-select';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import { getColumnDefinitionByType } from 'screens/performanceReview/manageAndInsights/TalentPlanning/columnDefinitions';

import { Minus } from 'assets/assetsV1/iconComponents';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { Avatar } from 'uiToolkitV1/Avatar';
import { AvatarGroup } from 'uiToolkitV1/AvatarGroup';
import { Divider } from 'uiToolkitV1/Divider';
import { InputV2 } from 'uiToolkitV1/InputV2';
import { SelectDashed } from 'uiToolkitV1/Select';
import { stylesForMenuPortalSelectDashed } from 'uiToolkitV1/Select/SelectDashed.styles';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { StakeholderErrorState, StakeholderEmptyState } from './shared/stakeholderComponents';
import { StakeHolderTableProps } from './shared/stakeholderTypes';
import { formatCurrency } from './shared/stakeholderUtils';
import { setModifiedApproverWiseAllocationRows, useCompensationDrawersState } from './slice/compensationDrawersSlice';

const customColumnPropsMap = {
  approver: {
    flex: 1,
    autoHeight: true,
  },
};

const SingleValueAdjustment = (props) => {
  const { data } = props;
  return <components.SingleValue {...props}>{data.icon}</components.SingleValue>;
};

const OptionAdjustment = (props) => {
  const { data } = props;
  return <components.Option {...props}>{data.icon}</components.Option>;
};

const BudgetAdjustmentCell = (params: any) => {
  const { data } = params;

  const { id } = data ?? {};

  const adjustmentOptions = [
    { value: 'ADD', label: 'Add', icon: <Add width="16px" height="16px" color="currentColor" /> },
    { value: 'SUBTRACT', label: 'Subtract', icon: <Minus width="16px" height="16px" color="currentColor" /> },
  ];
  const { modifiedApproverWiseAllocationRows } = useCompensationDrawersState();

  const [localAdjustedBudget, setLocalAdjustedBudget] = useState(data?.adjustedBudget || 0);
  const [isAdjustedBudgetFocused, setIsAdjustedBudgetFocused] = useState(false);

  const dispatch = useDispatch();

  const [selectedAdjustmentOption, setSelectedAdjustmentOption] = useState(adjustmentOptions[0]);

  const handleBudgetUpdate = (option = null) => {
    const newRows = modifiedApproverWiseAllocationRows.map(
      (row: { id: number; currentPayroll: number; increasedAllocation: number; totalAllocation: number }) => {
        if (row.id === id) {
          return {
            ...row,
            totalAllocation:
              (option?.value || selectedAdjustmentOption.value) === 'ADD'
                ? Number(row.currentPayroll) + Number(row.increasedAllocation) + Number(localAdjustedBudget)
                : Number(row.currentPayroll) + Number(row.increasedAllocation) - Number(localAdjustedBudget),
            adjustedBudget:
              (option?.value || selectedAdjustmentOption.value) === 'ADD'
                ? Number(localAdjustedBudget)
                : -Number(localAdjustedBudget),
          };
        }
        return row;
      }
    );
    dispatch(setModifiedApproverWiseAllocationRows(newRows));
  };

  return (
    <Box display="flex" width="300px" height="66px" padding="10px 12px" alignItems="center" gap="4px">
      <Box sx={{ '.select__control': { width: '45px !important', minWidth: '45px !important' } }}>
        <SelectDashed
          withBackground
          handleChange={(option) => {
            setSelectedAdjustmentOption(option);
            handleBudgetUpdate(option);
          }}
          components={{
            SingleValue: SingleValueAdjustment,
            Option: OptionAdjustment,
          }}
          variant="default"
          value={selectedAdjustmentOption}
          options={adjustmentOptions}
          isSearchable={false}
          menuPortalTarget={document.body}
          menuPortalStyles={stylesForMenuPortalSelectDashed}
          selectProps={{
            menuPortalTarget: document.body,
          }}
        />
      </Box>

      <InputV2
        value={isAdjustedBudgetFocused ? localAdjustedBudget : `$${localAdjustedBudget}`}
        isDashed
        onChange={(e) => {
          // Remove % sign and keep only numbers
          const numericAdjustedBudget = Number(e.target.value.replace('$', '').replace(/[^0-9.-]/g, ''));
          setLocalAdjustedBudget(numericAdjustedBudget);
        }}
        onFocus={() => setIsAdjustedBudgetFocused(true)}
        onBlur={() => {
          setIsAdjustedBudgetFocused(false);
          handleBudgetUpdate();
        }}
        inputMode="decimal"
      />
    </Box>
  );
};

// Add the Employees cell renderer component
const Employees = (data) => {
  const employees = data?.data?.employees ?? [];
  if (!employees || employees.length === 0) return null;

  return (
    <Box
      height="100%"
      sx={{
        '.chakra-avatar__group': {
          height: '100% !important',
        },
        '.chakra-avatar': {
          marginRight: '4px',
        },
      }}
    >
      <AvatarGroup>
        {employees?.map(({ displayName, profilePhoto, designation }) => (
          <Tooltip
            key={displayName}
            sx={{ background: '#595E6A', fontSize: '12px', color: '#ffffff' }}
            label={`${displayName}${designation ? ` - ${designation}` : ''}`}
          >
            <Flex>
              <Avatar name={displayName} src={profilePhoto} />
            </Flex>
          </Tooltip>
        ))}
      </AvatarGroup>
    </Box>
  );
};

const additionalColumns = [
  {
    columnId: 'EMPLOYEES',
    headerName: 'Employees',
    minWidth: 220,
    cellRenderer: (params: any) => <Employees data={params.data} />,
  },
  {
    columnId: 'currentPayroll',
    headerName: 'Current payroll',
    cellRenderer: (params: any) => <TextBodyMedium>{formatCurrency(params.data.currentPayroll)}</TextBodyMedium>,
  },
  {
    columnId: 'increasedAllocation',
    headerName: 'Increased allocation',
    cellRenderer: (params: any) => <TextBodyMedium>{formatCurrency(params.data.increasedAllocation)}</TextBodyMedium>,
  },
  {
    columnId: 'budgetAdjustment',
    headerName: 'Budget adjustment',
    cellRenderer: (params: any) => <BudgetAdjustmentCell data={params.data} />,
    width: 300,
  },
  {
    columnId: 'totalAllocation',
    headerName: 'Total allocation',
    cellRenderer: (params: any) => <TextBodyMedium>{formatCurrency(params.data.totalAllocation)}</TextBodyMedium>,
  },
];

const baseColumns = [
  {
    columnId: 'approver',
    headerName: 'Approver',
    field: 'approver',
    minWidth: 220,
    columnType: 'EMPLOYEE',
    displayName: 'Approver',
    description: 'Approver',
    metadata: { isEmployeeSubtitle: true, isEmployeeDesignation: true },
  },
];

function ApproverWiseAllocationTable({
  gridRef,
  gridStatePersistenceKey,
  loading = false,
  error = null,
}: StakeHolderTableProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentCompRef = useRef<any>(null);
  const agGridRef = gridRef ?? currentCompRef;
  const { modifiedApproverWiseAllocationRows } = useCompensationDrawersState();
  const [columnDefs, setColumnDefs] = useState([]);
  // Set up column definitions
  useEffect(() => {
    const newColumnDefs = baseColumns.map((col) => ({
      colId: col.columnId,
      field: col.field,
      id: col.columnId,
      displayName: col.displayName,
      description: col.description,
      headerName: col.displayName,
      minWidth: 220,
      ...getColumnDefinitionByType(col),
      ...(customColumnPropsMap[col.columnId] || {}),
    }));

    setColumnDefs([...newColumnDefs, ...additionalColumns]);
  }, []);
  const displayData = modifiedApproverWiseAllocationRows;

  // Error state handling
  if (error) {
    return (
      <StakeholderErrorState
        heading="No budget allocation details are available!"
        description="There was an issue fetching the budget allocation details. Please try again later."
      />
    );
  }

  return (
    <Box
      display="flex"
      padding="8px 8px 12px 8px"
      flexDirection="column"
      alignItems="flex-start"
      gap="12px"
      alignSelf="stretch"
      borderRadius="8px"
      background="#FFF"
    >
      {displayData?.length === 0 && !loading ? (
        <StakeholderEmptyState heading="No budget allocation details are available!" />
      ) : (
        <>
          <Box width="100%">
            <Box
              display="flex"
              padding="8px 0px"
              alignItems="flex-start"
              gap="0px"
              alignSelf="stretch"
              borderRadius="0px"
            >
              <TextBodyMedium bold>Current pay details</TextBodyMedium>
            </Box>
            <Divider color="#EBEFF3" />
          </Box>

          <Box className="stakeholder-flat-list-table" width="100%" height="calc(100vh - 265px)">
            <AgGridTable
              key={gridStatePersistenceKey}
              gridStatePersistenceKey={gridStatePersistenceKey}
              ref={agGridRef}
              getRowId={(params) => params.data.id}
              columnDefs={columnDefs}
              paginationAutoPageSize={false}
              headerHeight={40}
              rowHeight={66}
              sideBar={{
                position: 'right',
                toolPanels: ['columns'],
                defaultToolPanel: undefined,
              }}
              loading={loading}
              rowData={displayData}
              defaultColDef={{
                cellStyle: {
                  display: 'flex',
                  alignItems: 'center',
                },
                headerComponent: CustomHeaderCell,
              }}
            />
          </Box>
        </>
      )}
    </Box>
  );
}

export { ApproverWiseAllocationTable };
